import React from 'react';
import styles from './category5.module.scss';
import { Col, Row, Spin } from 'antd';
import {
  ScopeThreeData,
  ScopeWasteType,
} from 'models/ActivityDataEntryScopes/scopeThree.model';
import ScopeThreeHeader from 'shared/components/Scope3Header';
import { ReactComponent as CategoryFive } from 'assets/icons/waste_cat_5.svg';
import { useParams } from 'react-router-dom';
import { category5, scope3 } from 'constants/constants';
import ActivityDataEntryService from 'services/ActivityDataEntryService';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useRedirect from 'shared/utils/useRedirect';
import { ActivityDataEntryScopeTabs } from 'enums/ScopeThreeCategoryStatusEnums';
import Category5Layout from './category5Layout';
import { useYearLabel } from 'shared/utils/generateYearSpan';
import { scopeThreeCateogy5ValidationSchema } from './category5ValidationSchema';
import { ScopeThreeFormEditProvider } from 'context/ScopeThreeFormEditContext';
import { useScopeThreeCategoryUrlMode } from 'shared/hooks/useScopeThreeCategoryUrlMode';

const Category5 = () => {
  const { redirectToActivityDataEntry } = useRedirect();
  const { id: locationId } = useParams();
  const {
    adeParams,
    isEditView,
    enterEditMode,
    clearEditModeParams,
  } = useScopeThreeCategoryUrlMode();
  const { activeTab, isProcessingFacility: isPF } = adeParams;

  const {
    downloadScopeTemplate,
    loading,
    fetchCategory5,
    createScopeThreeForLocation,
  } = ActivityDataEntryService();

  const isProcessingFacility = isPF !== 'true';

  const fetchInitialValues = async (): Promise<ScopeThreeData> => {
    const values = await fetchCategory5({
      locationId: locationId ?? '',
      year: activeTab,
    });
    if (!values) {
      return {
        ...new ScopeThreeData(),
        scopeWasteType: [new ScopeWasteType()],
      };
    }
    return {
      ...values,
      scopeWasteType: values.scopeWasteType?.length
        ? values.scopeWasteType
        : [new ScopeWasteType()],
    };
  };

  const methods = useForm<ScopeThreeData>({
    defaultValues: fetchInitialValues,
    resolver: yupResolver(
      scopeThreeCateogy5ValidationSchema(isProcessingFacility)
    ),
    mode: 'onChange',
  });

  const { control, handleSubmit, trigger, reset } = methods;

  const handleCancelEdit = async () => {
    const fresh = await fetchInitialValues();
    reset(fresh);
    clearEditModeParams();
  };

  const onSubmit = async (values: ScopeThreeData) => {
    const cleanedData = {
      ...values,
      scopeWasteType: values?.scopeWasteType?.filter(
        item =>
          item?.totalWasteProduced || item?.treatmentMethod || item?.wasteTypeId
      ),
    };
    const response = await createScopeThreeForLocation(
      locationId,
      activeTab,
      category5,
      cleanedData
    );
    if (!response) return;
    const fresh = await fetchCategory5({
      locationId: locationId ?? '',
      year: activeTab,
    });
    if (fresh) {
      reset({
        ...fresh,
        scopeWasteType: fresh.scopeWasteType?.length
          ? fresh.scopeWasteType
          : [new ScopeWasteType()],
      });
    }
    clearEditModeParams();
    redirectToActivityDataEntry(
      locationId,
      {
        activeTab,
        scopeTab: ActivityDataEntryScopeTabs.SCOPE_3_UPSTREAM,
      },
      true
    );
  };

  const downloadTemplate = async () => {
    if (!locationId) return;
    await downloadScopeTemplate(locationId, activeTab, scope3, category5);
  };

  const uploadCallBack = async () => {
    if (locationId && activeTab) {
      const updatedValues = await fetchCategory5({
        locationId,
        year: activeTab,
      });
      reset({
        ...updatedValues,
        scopeWasteType: updatedValues?.scopeWasteType?.length
          ? updatedValues.scopeWasteType
          : [new ScopeWasteType()],
      });
    }
  };

  return (
    <ScopeThreeFormEditProvider isEditView={isEditView}>
      <div className={styles.pageWrapper}>
        <Spin spinning={loading} className={styles.loader}>
          <FormProvider {...methods}>
            <Row className={styles.contentContainer}>
              <Col span={24} className={styles.contentWrapper}>
                <ScopeThreeHeader
                  icon={<CategoryFive />}
                  title="Category 5- Waste Generated In Operations"
                  year={useYearLabel(activeTab)}
                  onSubmit={handleSubmit(onSubmit)}
                  isUploadExist
                  downloadTemplate={downloadTemplate}
                  uploadCallBack={uploadCallBack}
                  loading={loading}
                  category={category5}
                  isEditView={isEditView}
                  onEnterEdit={enterEditMode}
                  onCancelEdit={handleCancelEdit}
                />
              </Col>
              <Col span={24} className={styles.wasteTypeFormWrapper}>
                <Category5Layout control={control} trigger={trigger} />
              </Col>
            </Row>
          </FormProvider>
        </Spin>
      </div>
    </ScopeThreeFormEditProvider>
  );
};

export default Category5;
